/**
 * 回退对话框组件
 */

import { useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

import { getStageDisplayName } from '../workflow-constants'

import type { WorkflowStage, TrackType } from '@company/shared'

// ==================== 类型定义 ====================

interface RollbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  issueTitle: string
  currentStage: WorkflowStage
  rollbackStages: WorkflowStage[]
  onConfirm: (targetStage: WorkflowStage, reason: string) => void
  loading?: boolean
  error?: string | null
}

// ==================== 组件实现 ====================

export function RollbackDialog({
  open,
  onOpenChange,
  issueTitle,
  currentStage,
  rollbackStages,
  onConfirm,
  loading = false,
  error,
}: RollbackDialogProps) {
  const [targetStage, setTargetStage] = useState<WorkflowStage | ''>('')
  const [reason, setReason] = useState('')
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({})

  const validate = () => {
    const errors: Record<string, string> = {}
    if (!targetStage) {
      errors.targetStage = '请选择回退目标阶段'
    }
    if (!reason.trim()) {
      errors.reason = '请输入回退原因'
    }
    setLocalErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleConfirm = () => {
    if (!validate()) {
      return
    }
    onConfirm(targetStage as WorkflowStage, reason)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTargetStage('')
      setReason('')
      setLocalErrors({})
    }
    onOpenChange(open)
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>回退任务</AlertDialogTitle>
          <AlertDialogDescription>
            将任务「{issueTitle}」从{getStageDisplayName(currentStage)}回退
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {/* 目标阶段选择 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">
              回退到 <span className="text-destructive">*</span>
            </label>
            <Select
              value={targetStage || 'placeholder'}
              onValueChange={(value) =>
                setTargetStage(value === 'placeholder' ? '' : (value as WorkflowStage))
              }
            >
              <SelectTrigger
                className={cn('w-full', localErrors.targetStage && 'border-destructive')}
              >
                <SelectValue placeholder="请选择目标阶段" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="placeholder" disabled>
                  请选择目标阶段
                </SelectItem>
                {rollbackStages.map((stage) => (
                  <SelectItem key={stage} value={stage}>
                    {getStageDisplayName(stage)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {localErrors.targetStage && (
              <p className="text-destructive mt-1 text-sm">{localErrors.targetStage}</p>
            )}
          </div>

          {/* 回退原因 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">
              回退原因 <span className="text-destructive">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="请输入回退原因"
              rows={3}
              className={cn(
                'bg-background w-full rounded-md border px-3 py-2 text-sm',
                localErrors.reason && 'border-destructive'
              )}
            />
            {localErrors.reason && (
              <p className="text-destructive mt-1 text-sm">{localErrors.reason}</p>
            )}
          </div>

          {/* API 错误提示 */}
          {error && (
            <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">{error}</div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>取消</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? '回退中...' : '确认回退'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * 轨道回退对话框
 * 从测试阶段精准回退开发轨道
 */
interface TrackRollbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  frontendEnabled: boolean
  backendEnabled: boolean
  onConfirm: (tracks: TrackType[], reason: string) => void
  loading?: boolean
  error?: string | null
}

export function TrackRollbackDialog({
  open,
  onOpenChange,
  frontendEnabled,
  backendEnabled,
  onConfirm,
  loading = false,
  error,
}: TrackRollbackDialogProps) {
  const [selectedTracks, setSelectedTracks] = useState<TrackType[]>([])
  const [reason, setReason] = useState('')

  const toggleTrack = (track: TrackType) => {
    setSelectedTracks((prev) =>
      prev.includes(track) ? prev.filter((t) => t !== track) : [...prev, track]
    )
  }

  const handleConfirm = () => {
    if (selectedTracks.length === 0 || !reason.trim()) {
      return
    }
    onConfirm(selectedTracks, reason)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedTracks([])
      setReason('')
    }
    onOpenChange(open)
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>回退开发轨道</AlertDialogTitle>
          <AlertDialogDescription>
            选择需要回退的轨道，未选择的轨道将保持已通过状态。
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          {/* 轨道选择 */}
          <div className="space-y-2">
            <label className="text-sm font-medium">选择回退轨道</label>
            <div className="flex gap-4">
              {frontendEnabled && (
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedTracks.includes('frontend')}
                    onChange={() => toggleTrack('frontend')}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm">前端</span>
                </label>
              )}
              {backendEnabled && (
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedTracks.includes('backend')}
                    onChange={() => toggleTrack('backend')}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm">后端</span>
                </label>
              )}
            </div>
          </div>

          {/* 回退原因 */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              回退原因 <span className="text-destructive">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="请输入回退原因"
              rows={3}
              className="bg-background w-full rounded-md border px-3 py-2 text-sm"
            />
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">{error}</div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>取消</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={loading || selectedTracks.length === 0 || !reason.trim()}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {loading ? '回退中...' : '确认回退'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
